import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, 
  Sparkles, 
  Calendar, 
  Tag, 
  ChevronDown, 
  ChevronRight, 
  Search, 
  ChevronsUpDown,
  CheckCircle2,
  Wrench,
  Palette,
  Layers,
  ArrowUpRight
} from 'lucide-react';
import { APP_VERSION } from '../utils/version';
import { RELEASE_NOTES, groupReleaseNotesByMonth, formatReleaseDate } from '../data/releaseNotes';

export default function ReleaseNotesModal({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Filter release notes by search query if present
  const filteredNotes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return RELEASE_NOTES;

    return RELEASE_NOTES.filter((note) => {
      if (note.version.toLowerCase().includes(q)) return true;
      if (note.title.toLowerCase().includes(q)) return true;
      if (note.date.toLowerCase().includes(q)) return true;
      if (note.changes && note.changes.some((c) => c.text.toLowerCase().includes(q) || c.type.toLowerCase().includes(q))) {
        return true;
      }
      return false;
    });
  }, [searchQuery]);

  // Group filtered notes by month
  const monthlyGroups = useMemo(() => {
    return groupReleaseNotesByMonth(filteredNotes);
  }, [filteredNotes]);

  // Initialize expanded state: default to expanding the latest month (first group)
  useEffect(() => {
    if (monthlyGroups.length > 0) {
      setExpandedMonths((prev) => {
        // If empty or first open, open the latest month (or all if filtered)
        if (Object.keys(prev).length === 0) {
          const initial = {};
          // If searching, expand all matching groups; otherwise expand the first month
          monthlyGroups.forEach((group, index) => {
            initial[group.monthKey] = searchQuery ? true : index === 0;
          });
          return initial;
        }
        return prev;
      });
    }
  }, [monthlyGroups, searchQuery]);

  if (!isOpen) return null;

  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const areAllExpanded = monthlyGroups.length > 0 && monthlyGroups.every((g) => expandedMonths[g.monthKey]);

  const toggleAllMonths = () => {
    const nextState = !areAllExpanded;
    const updated = {};
    monthlyGroups.forEach((g) => {
      updated[g.monthKey] = nextState;
    });
    setExpandedMonths(updated);
  };

  const getChangeBadge = (type) => {
    switch (type) {
      case 'feat':
        return (
          <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">
            <Sparkles className="w-2.5 h-2.5" />
            <span>Feature</span>
          </span>
        );
      case 'fix':
        return (
          <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-500/15 text-sky-400 border border-sky-500/30 shrink-0">
            <Wrench className="w-2.5 h-2.5" />
            <span>Fix</span>
          </span>
        );
      case 'style':
        return (
          <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/15 text-purple-400 border border-purple-500/30 shrink-0">
            <Palette className="w-2.5 h-2.5" />
            <span>UI / Style</span>
          </span>
        );
      case 'refactor':
      default:
        return (
          <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 shrink-0">
            <Layers className="w-2.5 h-2.5" />
            <span>Update</span>
          </span>
        );
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-slate-950/80 backdrop-blur-md overflow-hidden animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="release-notes-modal-title"
      onClick={onClose}
    >
      <div 
        className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full flex flex-col max-h-[92vh] sm:max-h-[88vh] shadow-2xl shadow-sky-950/40 relative my-auto overflow-hidden text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-800/80 bg-slate-900/90 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-sky-500/15 text-sky-400 border border-sky-500/30 shadow-inner">
              <Sparkles className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 id="release-notes-modal-title" className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  Release Notes &amp; Updates
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-500/15 text-sky-400 border border-sky-500/30">
                  Current v{APP_VERSION}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Public feature additions, enhancements, and system fixes grouped by month
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition"
            aria-label="Close release notes"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar: Search & Expand/Collapse Toggle */}
        <div className="px-5 sm:px-6 py-3 bg-slate-950/40 border-b border-slate-800/70 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 shrink-0">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search release notes, features, or versions..."
              className="w-full pl-9 pr-8 py-1.5 bg-slate-900/90 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-0.5 rounded"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between sm:justify-end space-x-2 text-xs text-slate-400">
            <span className="text-[11px] text-slate-400">
              {filteredNotes.length} {filteredNotes.length === 1 ? 'release' : 'releases'} • {monthlyGroups.length} {monthlyGroups.length === 1 ? 'month' : 'months'}
            </span>
            <button
              type="button"
              onClick={toggleAllMonths}
              className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 text-slate-300 hover:text-white transition text-xs font-medium cursor-pointer"
            >
              <ChevronsUpDown className="w-3.5 h-3.5" />
              <span>{areAllExpanded ? 'Collapse All' : 'Expand All'}</span>
            </button>
          </div>
        </div>

        {/* Scrollable Release History List */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 max-h-[60vh] sm:max-h-[64vh]">
          {monthlyGroups.length === 0 ? (
            <div className="text-center py-12 px-4 rounded-xl border border-dashed border-slate-800">
              <Sparkles className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-300">No release notes found</p>
              <p className="text-xs text-slate-500 mt-1">
                No releases matched "{searchQuery}". Try a different keyword or version number.
              </p>
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="mt-3 text-xs text-sky-400 hover:text-sky-300 underline font-semibold cursor-pointer"
              >
                Reset search filter
              </button>
            </div>
          ) : (
            monthlyGroups.map((group) => {
              const isExpanded = !!expandedMonths[group.monthKey];
              const containsCurrentVersion = group.releases.some((r) => r.version === APP_VERSION);

              return (
                <div 
                  key={group.monthKey}
                  className="rounded-xl border border-slate-800/90 bg-slate-900/60 overflow-hidden shadow-sm transition"
                >
                  {/* Month Accordion Header */}
                  <button
                    type="button"
                    onClick={() => toggleMonth(group.monthKey)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/50 hover:bg-slate-800/80 border-b border-slate-800/70 transition text-left cursor-pointer group"
                    aria-expanded={isExpanded}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-1.5 rounded-lg border transition ${
                        containsCurrentVersion 
                          ? 'bg-sky-500/20 text-sky-400 border-sky-500/30' 
                          : 'bg-slate-800 text-slate-400 border-slate-700/60 group-hover:text-slate-200'
                      }`}>
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-sm sm:text-base text-white group-hover:text-sky-300 transition">
                          {group.monthLabel}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-800 text-slate-400 border border-slate-700">
                          {group.releases.length} {group.releases.length === 1 ? 'release' : 'releases'}
                        </span>
                        {containsCurrentVersion && (
                          <span className="hidden sm:inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>Current Stack</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 text-slate-400 group-hover:text-white transition">
                      <span className="text-xs font-medium hidden sm:inline">
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </span>
                      <ChevronDown 
                        className={`w-4 h-4 transform transition-transform duration-200 ${
                          isExpanded ? 'rotate-180 text-sky-400' : ''
                        }`} 
                      />
                    </div>
                  </button>

                  {/* Month Releases Accordion Content */}
                  {isExpanded && (
                    <div className="p-3 sm:p-4 space-y-3 bg-slate-950/20 divide-y divide-slate-800/60">
                      {group.releases.map((release) => {
                        const isCurrent = release.version === APP_VERSION;

                        return (
                          <div 
                            key={release.version}
                            className={`pt-3 first:pt-0 ${
                              isCurrent 
                                ? 'p-3 rounded-xl bg-sky-950/20 border border-sky-500/25 mb-3 shadow-inner' 
                                : ''
                            }`}
                          >
                            {/* Release Title & Version Row */}
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-mono text-xs font-bold border ${
                                  isCurrent
                                    ? 'bg-sky-500/20 text-sky-300 border-sky-400/40 shadow-sm'
                                    : 'bg-slate-800/90 text-slate-200 border-slate-700'
                                }`}>
                                  v{release.version}
                                </span>
                                {isCurrent && (
                                  <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-500 text-slate-950 shadow-sm">
                                    <CheckCircle2 className="w-3 h-3 text-slate-950" />
                                    <span>Latest</span>
                                  </span>
                                )}
                                <h3 className="font-semibold text-xs sm:text-sm text-slate-100">
                                  {release.title}
                                </h3>
                              </div>

                              <div className="flex items-center space-x-1.5 text-slate-400 text-xs shrink-0">
                                <Calendar className="w-3 h-3 text-slate-500" />
                                <time dateTime={release.date}>
                                  {formatReleaseDate(release.date)}
                                </time>
                              </div>
                            </div>

                            {/* Release Changes List */}
                            <ul className="space-y-1.5 pl-1">
                              {release.changes.map((change, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-xs leading-relaxed text-slate-300">
                                  <div className="mt-0.5">
                                    {getChangeBadge(change.type)}
                                  </div>
                                  <span className="text-slate-300">
                                    {change.text}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-800/80 bg-slate-900/90 flex items-center justify-between shrink-0">
          <div className="text-[11px] text-slate-500 flex items-center space-x-1">
            <span>Semantic Versioning: Major • Minor • Patch</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-semibold text-white transition cursor-pointer shadow-sm"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
